import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, FilePenLine, MessagesSquare } from 'lucide-react';
import './QuoteProcessInfo.css';

const guides = {
  home: {
    eyebrow: 'Project quotes',
    title: 'One request. Multiple seller quotes.',
    description: 'Source project materials that need custom pricing, delivery terms or a seller recommendation.',
    steps: [
      ['Describe the project', 'Add the material, quantity, location, fulfillment preference and reference photos.'],
      ['Sellers prepare quotes', 'Eligible sellers review the request and respond with their price, costs and estimated delivery date.'],
      ['Choose with confidence', 'Compare every response in one place and accept the quote that best fits your project.'],
    ],
  },
  seller: {
    eyebrow: 'Seller guide',
    title: 'How seller quotes work',
    description: 'Turn relevant buyer requests into new sales without creating a product listing first.',
    steps: [
      ['Find a matching request', 'Filter active requests by material, category, location, budget or date, then open one to review the full project.'],
      ['Submit your best offer', 'Provide one clear quote with the material or service price, shipping, taxes and a specific expected delivery date. You can update it while the request is active.'],
      ['Continue from Sales', 'If the buyer accepts your quote, the request closes and becomes an order. Manage payment and fulfillment from the Sales tab.'],
    ],
  },
  buyer: {
    eyebrow: 'Buyer guide',
    title: 'How buyer quote requests work',
    description: 'Request custom project pricing and compare seller responses before committing to an order.',
    steps: [
      ['Create a request', 'Use the Request a Quote button to add your material, quantity, location, fulfillment preference and project photos. Requests remain active for 7 days.'],
      ['Compare seller responses', 'Open a request under My Requests to compare each seller, quoted total, included costs and estimated delivery date.'],
      ['Accept and track', 'Accept the quote that works for you. Eulua converts it into an order so you can continue with payment and fulfillment tracking.'],
    ],
  },
};

const QuoteProcessInfo = ({ audience = 'home' }) => {
  const guide = guides[audience] || guides.home;
  const titleId = `quote-process-info-${audience}`;

  return (
    <section className={`quote-process-info quote-process-info--${audience}`} aria-labelledby={titleId}>
      <div className="quote-process-info__intro">
        <div className="quote-process-info__icon" aria-hidden="true">
          <MessagesSquare size={25} />
        </div>
        <p className="quote-process-info__eyebrow">{guide.eyebrow}</p>
        <h2 id={titleId}>{guide.title}</h2>
        <p className="quote-process-info__description">{guide.description}</p>
        {audience === 'home' && (
          <Link className="quote-process-info__link" to="/sell/dashboard?tab=quotes">
            Explore seller quote requests <ArrowRight size={17} />
          </Link>
        )}
      </div>

      <ol className="quote-process-info__steps">
        {guide.steps.map(([title, description], index) => (
          <li key={title}>
            <span className="quote-process-info__step-number">{index + 1}</span>
            <div>
              <h3>{title}</h3>
              <p>{description}</p>
            </div>
            <CheckCircle2 className="quote-process-info__check" size={19} aria-hidden="true" />
          </li>
        ))}
      </ol>

      {audience === 'home' && (
        <FilePenLine className="quote-process-info__decoration" size={180} aria-hidden="true" />
      )}
    </section>
  );
};

export default QuoteProcessInfo;
